import PointSymbol3D from "@arcgis/core/symbols/PointSymbol3D";
import { WORLD_MAP_CLUSTER_COLOR, WORLD_MAP_CLUSTER_CONTRAST_COLOR, WORLD_MAP_CLUSTER_OUTLINE_COLOR, WORLD_MAP_CLUSTER_OUTLINE_SIZE, WORLD_MAP_CLUSTER_SIZE_STEPS } from "@components/world-map/clustering/world-map-cluster-constants";
import { getMarkerSymbol3D, MARKER_3D_VERTICAL_OFFSET } from "@components/world-map/world-map-constants";

/** The two marker states the tilt threshold flips between. */

/**
 * Constructs a symbol from autocast properties. ArcGIS autocasting reads `type`
 * to choose the class and drops it; the constructor instead rejects it as
 * read-only, so it has to be stripped here.
 */
function toPointSymbol3D(properties) {
  const symbolProperties = {
    ...properties
  };
  delete symbolProperties.type;
  return new PointSymbol3D(symbolProperties);
}

/** Size step for a cluster with the given number of members. */
function getClusterSizeStep(count) {
  return WORLD_MAP_CLUSTER_SIZE_STEPS.find(step => count >= step.minCount) ?? WORLD_MAP_CLUSTER_SIZE_STEPS[WORLD_MAP_CLUSTER_SIZE_STEPS.length - 1];
}

/**
 * Builds the cluster bubble: a filled circle with the member count inside,
 * sized by `count` (see {@link WORLD_MAP_CLUSTER_SIZE_STEPS}).
 *
 * `IconSymbol3DLayer` renders the circle from a built-in primitive and
 * `TextSymbol3DLayer` draws the count in the same symbol, so no icon image has to
 * be generated or cached.
 *
 * The vertical offset is what keeps the bubble visible: sitting flush on the
 * surface it gets occluded by terrain and buildings from close range. Being
 * screen-anchored, it holds the same pixel lift at every zoom.
 *
 * Pass a different `color` to recolour the bubble, e.g. while it is highlighted.
 */
export function createClusterSymbol(label, count, color = WORLD_MAP_CLUSTER_COLOR) {
  const {
    size,
    textSize
  } = getClusterSizeStep(count);
  return new PointSymbol3D({
    verticalOffset: MARKER_3D_VERTICAL_OFFSET,
    symbolLayers: [{
      type: "icon",
      resource: {
        primitive: "circle"
      },
      material: {
        color
      },
      outline: {
        color: WORLD_MAP_CLUSTER_OUTLINE_COLOR,
        size: WORLD_MAP_CLUSTER_OUTLINE_SIZE
      },
      size
    }, {
      type: "text",
      text: label,
      material: {
        color: WORLD_MAP_CLUSTER_CONTRAST_COLOR
      },
      size: textSize,
      // Not bold: ArcGIS pads bold and italic text on the right only, which
      // pushes the count off centre.
      font: {
        weight: "normal"
      },
      horizontalAlignment: "center",
      verticalAlignment: "middle"
    }]
  });
}

/**
 * Builds the grounded and lifted marker symbols once, so the tilt flip can swap
 * every graphic to a shared instance instead of constructing a symbol per pin.
 */
export function createMarkerSymbols() {
  return {
    grounded: toPointSymbol3D(getMarkerSymbol3D(false)),
    lifted: toPointSymbol3D(getMarkerSymbol3D(true))
  };
}